import { z } from 'zod'
import { dataTableConfig } from './config'
import type { TableSearchState } from './types'

export type CreateTableSearchSchemaOptions = {
  defaultPageSize?: number
  maxPageSize?: number
  /** When set, unknown sort column ids are stripped (not rejected). */
  sortIds?: readonly string[]
  /** When set, unknown filter keys are stripped. */
  filterKeys?: readonly string[]
  /** Filter keys whose values are comma-separated multi-selects. */
  multiFilterKeys?: readonly string[]
}

/**
 * Zod schema for TanStack Router `validateSearch`. Invalid values fall back
 * safely (`.catch`) so refresh / shared links never hard-crash.
 */
export function createTableSearchSchema(
  options: CreateTableSearchSchemaOptions = {},
) {
  const defaultPageSize =
    options.defaultPageSize ?? dataTableConfig.defaultPageSize
  const maxPageSize = options.maxPageSize ?? dataTableConfig.maxPageSize
  const sortAllow = options.sortIds ? new Set(options.sortIds) : null
  const filterAllow = options.filterKeys ? new Set(options.filterKeys) : null

  const page = z.coerce.number().int().min(1).catch(1).default(1)

  const pageSize = z.coerce
    .number()
    .int()
    .min(1)
    .max(maxPageSize)
    .catch(defaultPageSize)
    .default(defaultPageSize)

  const q = z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .transform((v) => (v?.trim() ? v.trim() : undefined))

  const sort = z
    .string()
    .max(500)
    .optional()
    .catch(undefined)
    .transform((raw) => {
      if (!raw?.trim()) return undefined
      if (!sortAllow) return raw
      const parts = raw
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
        .filter((p) => {
          const id = p.split(':')[0]
          return id && sortAllow.has(id)
        })
      return parts.length ? parts.join(',') : undefined
    })

  const cols = z
    .string()
    .max(500)
    .optional()
    .catch(undefined)
    .transform((v) => (v?.trim() ? v.trim() : undefined))

  const selected = z
    .string()
    .max(200)
    .optional()
    .catch(undefined)
    .transform((v) => (v?.trim() ? v.trim() : undefined))

  const filters = z
    .record(z.string(), z.string())
    .optional()
    .catch(undefined)
    .transform((raw) => {
      if (!raw) return undefined
      const out: Record<string, string> = {}
      for (const [k, v] of Object.entries(raw)) {
        if (filterAllow && !filterAllow.has(k)) continue
        if (v == null || v === '') continue
        out[k] = String(v)
      }
      return Object.keys(out).length ? out : undefined
    })

  return z.object({
    page,
    pageSize,
    q,
    sort,
    cols,
    selected,
    filters,
  })
}

export type TableSearchSchema = ReturnType<typeof createTableSearchSchema>
export type ParsedTableSearch = z.infer<TableSearchSchema>

/**
 * Normalize partial updates for navigate({ search }).
 *
 * A key is only carried over from `current` when the patch omits it. Testing
 * `patch.x !== undefined` instead conflated "key absent" with "key present and
 * undefined", so clearing a value was impossible: `{ selected: undefined }`
 * (closing a detail sheet) and `{ q: undefined }` (clearing search) both kept
 * the old value and the sheet never closed.
 */
export function mergeTableSearch(
  current: TableSearchState,
  patch: Partial<TableSearchState>,
  options?: { resetPageOnFilter?: boolean },
): TableSearchState {
  const has = (key: keyof TableSearchState) => Object.hasOwn(patch, key)

  const filterChanged =
    options?.resetPageOnFilter !== false &&
    (has('q') || has('filters') || has('sort') || has('pageSize'))

  const page =
    patch.page !== undefined ? patch.page : filterChanged ? 1 : current.page

  return {
    page,
    pageSize: patch.pageSize ?? current.pageSize,
    q: has('q') ? patch.q || undefined : current.q,
    sort: has('sort') ? patch.sort || undefined : current.sort,
    cols: has('cols') ? patch.cols || undefined : current.cols,
    selected: has('selected') ? patch.selected || undefined : current.selected,
    filters: has('filters')
      ? patch.filters && Object.keys(patch.filters).length
        ? patch.filters
        : undefined
      : current.filters,
  }
}

/** Strip empty optionals for cleaner URLs when writing search. */
export function compactTableSearch(search: TableSearchState): TableSearchState {
  return {
    page: search.page <= 1 ? 1 : search.page,
    pageSize: search.pageSize,
    q: search.q || undefined,
    sort: search.sort || undefined,
    cols: search.cols || undefined,
    selected: search.selected || undefined,
    filters:
      search.filters && Object.keys(search.filters).length
        ? search.filters
        : undefined,
  }
}
